import { CorporationType, Country } from '@commenda/prisma';

export const getPrintableCorporationType = (
  corporationType: CorporationType,
  // Certain corp types have different names in different countries - this allows us to override them.
  country: Country
) => {
  const corporationTypeVsLabelMap: Record<CorporationType, string> = {
    [CorporationType.CCorp]: 'C Corp',
    [CorporationType.Corporation]: 'Corporation',
    [CorporationType.FCZ]: 'FZC',
    [CorporationType.LLC]: 'LLC',
    [CorporationType.LLP]: 'LLP',
    [CorporationType.PteLtd]: 'Pte Ltd',
    [CorporationType.PvtLtd]: 'Pvt Ltd',
    [CorporationType.Unknown]: 'Other',
    [CorporationType.LTD]: 'Ltd',
    [CorporationType.BV]: 'B.V.',
    [CorporationType.SL]: 'SL',
    [CorporationType.SRL]: 'SRL',
    [CorporationType.SARL]: 'SARL',
    [CorporationType.OOD]: 'OOD',
    [CorporationType.DomesticCorporation]: 'Domestic Corporation',
    [CorporationType.SRO]: 'S.R.O',
    [CorporationType.DOO]: 'D.O.O',
    [CorporationType.AS]: 'AS',
    [CorporationType.LtdSti]: 'Ltd. Şti.',
    [CorporationType.Spzoo]: 'Sp. z o.o.',
    [CorporationType.PtyLtd]: 'Pty Ltd',
    [CorporationType.GmbH]: 'GmbH',
    [CorporationType.SAS]: 'SAS',
    [CorporationType.PJSC]: 'PJSC',
    [CorporationType.FZE]: 'FZE',
    [CorporationType.CivilCompany]: 'Civil Company',
    [CorporationType.ForeignBranch]: 'Foreign Branch',
    [CorporationType.NZUnltd]: 'Unlimited Company',
    [CorporationType.NZLP]: 'Limited Partnership (LP)',
    [CorporationType.NZGovtEntity]: 'Government Entity',
    [CorporationType.NZCoop]: 'Cooperative Company',
    // Content-driven entity types (keys match CorporationType enum in schema; cast for older Prisma client)
    NonprofitCorporation: 'Nonprofit Corporation',
    PublicLtd: 'Public Ltd',
    OPC: 'OPC',
    Section8: 'Section 8',
    SGPublicLtd: 'Public Company (Ltd)',
    CLG: 'Company Limited by Guarantee',
    SoleProprietorship: 'Sole Proprietorship',
    PublicPJSC: 'Public PJSC',
    FZC: 'FZC',
    Unltd: 'Unlimited',
    LimitedPartnership: 'Limited Partnership',
    GovernmentEntity: 'Government Entity',
    COOP: 'Cooperative',
    // Philippines entity types
    BranchOffice: 'Branch Office',
    CloseCorporation: 'Close Corporation',
    EducationalCorporation: 'Educational Corporation',
    NonstockCorporation: 'Non-Stock Corporation',
    OnePersonCorporation: 'One Person Corporation',
    Partnership: 'Partnership',
    ReligiousCorporation: 'Religious Corporation',
    StockCorporation: 'Stock Corporation',
    // Switzerland entity types
    Association: 'Association',
    Branch: 'Branch (Zweigniederlassung)',
    ContractualInvestmentFund: 'Contractual Investment Fund (Anlagefonds)',
    Cooperative: 'Cooperative',
    Foundation: 'Foundation',
    GeneralPartnership: 'General Partnership',
    InvestmentCompanyWithFixedCapital:
      'SICAF (Investment Company with Fixed Capital)',
    InvestmentCompanyWithVariableCapital:
      'SICAV (Investment Company with Variable Capital)',
    LimitedLiabilityCompany: 'Limited Liability Company',
    LimitedPartnershipForCollectiveInvestments:
      'KmGK (Limited Partnership for Collective Investments)',
    PartnershipLimitedByShares: 'Partnership Limited by Shares',
    PublicLawInstitution: 'Public Law Institution',
    SimplePartnership: 'Simple Partnership',
    // British Virgin Islands entity types
    BusinessCompanyLimitedByShares: 'Business Company Limited by Shares',
    BusinessCompanyLimitedByGuaranteeWithShares:
      'Business Company Limited by Guarantee (with shares)',
    BusinessCompanyLimitedByGuaranteeNoShares:
      'Business Company Limited by Guarantee (no shares)',
    UnlimitedCompanyWithShares: 'Unlimited Company (with shares)',
    UnlimitedCompanyNoShares: 'Unlimited Company (no shares)',
    SegregatedPortfolioCompany: 'Segregated Portfolio Company (SPC)',
    RestrictedPurposesCompany: 'Restricted Purposes Company (RPC)',
    RestrictedPurposeSegregatedPortfolioCompany: 'Restricted Purpose SPC',
    ForeignCompany: 'Foreign Company',
    SA: 'S.A.',
    SociedadPorAcciones: 'SpA',
    EIRL: 'E.I.R.L.',
    EmpresaUnipersonal: 'E.U.',
    NV: 'NV',
    AB: 'AB (Aktiebolag)',
    SdeRL: 'S. de R.L.',
    SdeRLdeCV: 'S. de R.L. de C.V.',
    KK: 'Kabushiki Kaisha (KK)',
    GK: 'Godo Kaisha (GK)',
    YK: 'Yugen Kaisha (YK)',
    PersonalLiabilityCompany: 'Personal Liability Company (Inc)',
    InternationalCompany: 'International Company',
    InternationalPartnership: 'International Partnership',
    InternationalTrust: 'International Trust',
    IncorporatedSociety: 'Incorporated Society',
    SpA: 'S.p.A.',
    AG: 'AG (Aktiengesellschaft)',
    UG: 'UG (haftungsbeschränkt)',
    PT: 'PT (Perseroan Terbatas)',
    PTPMA: 'PT PMA',
    JusikHoesa: 'Jusik Hoesa (Stock Company)',
    YuhanHoesa: 'Yuhan Hoesa (Limited Company)',
    JSC: 'Joint Stock Company',
    Trust: 'Trust',
    SCI: 'SCI (Société Civile Immobilière)',
    CIC: 'Community Interest Company (CIC)',
    AnonimSirket: 'A.Ş. (Anonim Şirket)',
    SingleMemberStockCompany: 'Single-Member Stock Company',
    CapitalAndIndustryPartnership: 'Sociedad de Capital e Industria',
    SingleMemberLimitedLiabilityCompany: 'Single-Member Limited Liability Company',
    Kft: 'Kft',
    SdnBhd: 'Sdn Bhd',
    SAdeCV: 'S.A. de C.V.',
    Ltda: 'Ltda.',
    SociedadeAnonima: 'S.A. (Sociedade Anônima)',
  } as Record<CorporationType, string>;

  const corporationTypeNameOverrides: Partial<
    Record<Country, Partial<Record<CorporationType, string>>>
  > = {
    // We're using CorporationType.PvtLtd to represent UK Private Limited companies, but the correct acronym there is 'Ltd'.
    [Country.CV]: {
      [CorporationType.Ltda]: 'Sociedade por Quotas (Lda.)',
      [CorporationType.SingleMemberLimitedLiabilityCompany]: 'Sociedade Unipessoal por Quotas',
      [CorporationType.SociedadeAnonima]: 'Sociedade Anónima (S.A.)',
      [CorporationType.SingleMemberStockCompany]: 'Sociedade Anónima Unipessoal',
    },
    [Country.AR]: {
      [CorporationType.SingleMemberStockCompany]: 'Sociedad Anónima Unipersonal (S.A.U.)',
      [CorporationType.CapitalAndIndustryPartnership]: 'Sociedad de Capital e Industria',
    },
    [Country.GB]: {
      [CorporationType.PvtLtd]: 'Ltd',
    },
    // NZ Limited Companies use the generic LTD type but need a more descriptive display name
    [Country.NZ]: {
      [CorporationType.LTD]: 'Limited Company (Ltd)',
    },
    // Cook Islands reuses the generic Corporation enum value for its domestic company
    [Country.CK]: {
      [CorporationType.Corporation]: 'Cook Islands Company',
    },
    // Swiss forms keep their German local terms; the shared enum values render generic labels elsewhere
    [Country.CH]: {
      [CorporationType.Association]: 'Association (Verein)',
      [CorporationType.Cooperative]: 'Cooperative (Genossenschaft)',
      [CorporationType.Foundation]: 'Foundation (Stiftung)',
      [CorporationType.GeneralPartnership]:
        'General Partnership (Kollektivgesellschaft)',
      [CorporationType.LimitedLiabilityCompany]:
        'GmbH / Sàrl (Limited Liability Company)',
      [CorporationType.SimplePartnership]:
        'Simple Partnership (Einfache Gesellschaft)',
    },
  };

  if (corporationTypeNameOverrides[country]?.[corporationType]) {
    return corporationTypeNameOverrides[country][corporationType];
  }

  return corporationTypeVsLabelMap[corporationType];
};
